import type { VehicleAttributeType } from './../data/models/vehicle-attribute-type.model';
import { AsyncSubject, type Observable } from 'rxjs';
import httpService from './httpService';
import type { TransportationType } from '../data/models/transportation-type.model';
import type { LegalEntityStatus } from '../data/models/legal-entity-status.model';
import type { LegalEntityType } from '../data/models/legal-entity-type.model';
import type { StateCode } from '../data/models/state-code.model';
import type { CountryList } from '../data/models/country-list.model';
import type { InsuranceStrength } from '../data/models/insurance-strength.model';
import type { MessagingMethod } from '../data/models/messaging-method.model';
import type { VehicleColor } from '../data/models/vehicle-color.model';
import type { VehicleType } from '../data/models/vehicle-type.model';
import type { ApplicationSetting } from '../data/models/application-setting.model';
import type {
  ProviderDocumentType,
  DriverDocumentType,
  VehicleDocumentType,
} from '../data/models/document-type.model';
import type { ProviderNoteCategory } from '../data/models/provider-note-category.model';
import type { ApplicationTitle } from '../data/models/application-title.model';
import type { UserCategoryType } from '../data/models/user-category-type.model';
import type { TripLegPackageRejectReason } from '../data/models/trip-leg-package-reject-reason.model';
import type { PaymentType } from '../data/models/payment-type.model';
import type { ChargeType } from '../data/models/charge-type.model';
import type { TripLegException } from '../data/models/trip-leg-exception.model';
import { TripLegStatusReference } from '../data/models/trip-leg-status.model';

class ReferenceService {
  private readonly apiBase;

  constructor() {
    const { __environment__ } = window as unknown as {
      __environment__: { REACT_APP_LOOKUP_API_URL: string };
    };
    this.apiBase = __environment__.REACT_APP_LOOKUP_API_URL.trim();
  }

  public getTransportationType(): Observable<TransportationType[]> {
    return httpService.getJSON(`${this.apiBase}/transportationType`);
  }

  public getLegalEntityStatuses(): Observable<LegalEntityStatus[]> {
    return httpService.getJSON(`${this.apiBase}/legalEntityStatus`);
  }

  public getLegalEntityTypes(): Observable<LegalEntityType[]> {
    return httpService.getJSON(`${this.apiBase}/legalEntityType`);
  }

  public getStateCodes(): Observable<StateCode[]> {
    return httpService.getJSON(`${this.apiBase}/stateCodeList`);
  }

  public getCountryList(): Observable<CountryList[]> {
    // return httpService.getJSON(`${this.apiBase}/countryList`);
    // this lookup table is going away so we're hard coding this for now
    // and will replace phone component post-MVP
    const subject = new AsyncSubject<CountryList[]>();
    subject.next([
      {
        countryListID: 1,
        countryPhoneCode: 1,
        countryName: 'United States',
        maxPhoneNumberLength: 10,
        isDefaultCountry: true,
        isActive: true,
      },
    ]);
    subject.complete();
    return subject;
  }

  public getInsuranceStrengths(): Observable<InsuranceStrength[]> {
    return httpService.getJSON(`${this.apiBase}/insuranceStrength`);
  }

  public getMessagingMethods(): Observable<MessagingMethod[]> {
    return httpService.getJSON(`${this.apiBase}/messagingMethod`);
  }

  public getVehicleAttributeTypes(): Observable<VehicleAttributeType[]> {
    return httpService.getJSON(`${this.apiBase}/vehicleAttributeType`);
  }

  public getVehicleColors(): Observable<VehicleColor[]> {
    return httpService.getJSON(`${this.apiBase}/vehicleColor`);
  }

  public getProviderDocumentTypes(): Observable<ProviderDocumentType[]> {
    return httpService.getJSON(
      `${this.apiBase}/transportationProviderDocumentType`,
    );
  }

  public getDriverDocumentTypes(): Observable<DriverDocumentType[]> {
    return httpService.getJSON(`${this.apiBase}/driverDocumentType`);
  }

  public getVehicleDocumentTypes(): Observable<VehicleDocumentType[]> {
    return httpService.getJSON(`${this.apiBase}/vehicleDocumentType`);
  }

  public getVehicleTypes(): Observable<VehicleType[]> {
    return httpService.getJSON(`${this.apiBase}/vehicleType`);
  }

  public getProviderNoteCategories(): Observable<ProviderNoteCategory[]> {
    return httpService.getJSON(`${this.apiBase}/providerNoteCategory`);
  }

  public getApplicationTitles(): Observable<ApplicationTitle[]> {
    return httpService.getJSON(`${this.apiBase}/applicationTitle`);
  }

  public getUserTypes(): Observable<UserCategoryType[]> {
    return httpService.getJSON(`${this.apiBase}/userCategoryType`);
  }

  public getApplicationSettings(): Observable<ApplicationSetting[]> {
    return httpService.getJSON(`${this.apiBase}/applicationSetting`);
  }

  public getTimeZones() {
    return httpService.getJSON(`${this.apiBase}/timeZone`);
  }

  public getFrequencyDayOfWeeks() {
    return httpService.getJSON(`${this.apiBase}/frequencyDayOfWeek`);
  }

  public getTripModes() {
    return httpService.getJSON(`${this.apiBase}/tripMode`);
  }

  public getTripLegPackageRejectReasons(): Observable<
    TripLegPackageRejectReason[]
  > {
    return httpService.getJSON(`${this.apiBase}/tripLegPackageRejectReason`);
  }

  public getPassengerCareLevels(): Observable<any[]> {
    return httpService.getJSON(`${this.apiBase}/passengerCareLevel`);
  }

  public getPaymentTypes(): Observable<PaymentType[]> {
    return httpService.getJSON(`${this.apiBase}/paymentType`);
  }

  public getChargeTypes(): Observable<ChargeType[]> {
    return httpService.getJSON(`${this.apiBase}/chargeType`);
  }

  public getTripLegStatuses(): Observable<TripLegStatusReference[]> {
    return httpService.getJSON(`${this.apiBase}/tripLegStatus`);
  }

  public getTripLegExceptions(): Observable<TripLegException[]> {
    return httpService.getJSON(`${this.apiBase}/tripLegException`);
  }
}

const referenceService = new ReferenceService();

export default referenceService;
